import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiDownload, FiEye, FiFileText, FiMoreHorizontal } from "react-icons/fi";
import ClientDocumentCard from "./ClientDocumentCard";

function formatSize(bytes) {
  if (!Number.isFinite(Number(bytes))) return "—";
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function categoryLabel(value) {
  return String(value || "general").replaceAll("_", " ");
}

function uploaderName(document, currentUserId, currentUserName) {
  if (document.uploaded_by === currentUserId) return currentUserName || "You";
  return "Business team";
}

export default function ClientDocumentTable({
  documents,
  loading,
  page,
  pageSize,
  totalCount,
  currentUserId,
  currentUserName,
  canManage,
  busyDocumentId,
  onPageChange,
  onPreview,
  onDownload,
  onDetails,
  onArchive,
}) {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const uploader = (doc) => uploaderName(doc, currentUserId, currentUserName);
  const actions = (doc) => (
    <div className="flex items-center justify-end gap-1">
      <button type="button" title="Preview" onClick={() => onPreview(doc)} className="rounded-lg p-2 text-slate-500 hover:bg-violet-50 hover:text-violet-700"><SafeIcon icon={FiEye} /></button>
      <button type="button" title="Download" disabled={busyDocumentId === doc.id} onClick={() => onDownload(doc)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"><SafeIcon icon={FiDownload} /></button>
      <button type="button" title="View details" onClick={() => onDetails(doc)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiMoreHorizontal} /></button>
      {canManage && <button type="button" disabled={busyDocumentId === doc.id} onClick={() => onArchive(doc)} className="rounded-lg px-2 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50">Archive</button>}
    </div>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Documents</h2>
          {!loading && <p className="mt-1 text-xs text-slate-400">{totalCount} active {totalCount === 1 ? "document" : "documents"}</p>}
        </div>
        <SafeIcon icon={FiFileText} className="text-lg text-violet-600" />
      </div>

      {loading ? (
        <div className="space-y-3 p-5" aria-live="polite">
          {[1, 2, 3].map((row) => <div key={row} className="h-12 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : documents.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <SafeIcon icon={FiFileText} className="mx-auto text-3xl text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-700">No documents found</p>
          <p className="mt-1 text-xs text-slate-500">Upload a document or adjust the filters.</p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-5 py-3">File name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Uploaded by</th><th className="px-4 py-3">Upload date</th><th className="px-4 py-3">File size</th><th className="px-4 py-3">Visibility</th><th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50/70">
                    <td className="max-w-[240px] px-5 py-4"><div className="flex items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-700"><SafeIcon icon={FiFileText} /></span><button type="button" onClick={() => onDetails(doc)} className="truncate text-left font-semibold text-slate-800 hover:text-violet-700">{doc.file_name}</button></div></td>
                    <td className="px-4 py-4 capitalize text-slate-600">{categoryLabel(doc.category)}</td>
                    <td className="px-4 py-4 text-slate-600">{uploader(doc)}</td>
                    <td className="px-4 py-4 text-slate-600">{new Date(doc.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-4 tabular-nums text-slate-600">{formatSize(doc.file_size)}</td>
                    <td className="px-4 py-4"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${doc.visibility === "shared_with_accountant" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{doc.visibility === "shared_with_accountant" ? "Shared" : "Private"}</span></td>
                    <td className="px-4 py-3">{actions(doc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-3 p-4 md:hidden">
            {documents.map((doc) => <ClientDocumentCard key={doc.id} document={doc} uploader={uploader(doc)} onPreview={onPreview} onDownload={onDownload} onDetails={onDetails} onArchive={onArchive} canManage={canManage} busy={busyDocumentId === doc.id} />)}
          </div>
        </>
      )}

      {!loading && totalCount > pageSize && (
        <footer className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
          <p className="text-xs text-slate-500">Page {page} of {pageCount}</p>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 disabled:opacity-40">Next</button>
          </div>
        </footer>
      )}
    </section>
  );
}